import { useDashboardOrder } from '@features/base/private/hooks/useDashboardOrder';
import { Widget } from '@features/dashboard/types';
import { useStreamV2DashboardService } from '@hooks/useStreamV2DashboardService';
import { useStreamV2Status } from '@hooks/useStreamV2Status';
import { Status } from '@services/StreamV2';
import { Dashboard } from '@shared/protos/v2/dashboard';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type DashboardState = {
  currentDashboard: Dashboard | undefined;
  allDashboards: Dashboard[];
  setCurrentDashboardById: (id: string) => void;
  addDashboard: (overrideDashboardName?: string) => Promise<Dashboard>;
  deleteDashboard: (id: string) => void;
  updateDashboard: (dashboard: Dashboard) => Promise<Dashboard>;
  editWidgetPayloadById: (id: string, newPayload: Object) => void;
  deleteWidgetById: (id: string) => void;
  addNewWidgets: (newWidgets: Widget[]) => void;
  clearCurrentDashboard: () => void;
  isDashboardLoading: boolean;
  editDashboardLayouts: (layouts: any) => void;
  needsFittingWidgets: boolean;
  setNeedsFittingWidgets: (val: boolean) => void;
  sortedDashboardList: Dashboard[];
  updateDashboardOrder: (newOrder: Dashboard[]) => void;
  addNewDashboardToOrder: (newDashboard: Dashboard) => void;
  removeDashboardFromOrder: (dashboardId: number) => void;
  dashboardOrderLoading: boolean;
  createDefaultDashboard: () => Promise<Dashboard | null | undefined>;
  loadDashboard: (dashboard: Dashboard) => Promise<Dashboard>;
  selectedFreemiumDashboard: number;
  setSelectedFreemiumDashboard: (id: number) => void;
};

type Props = {
  children: ReactNode;
};

const DashboardContext = createContext<DashboardState | undefined>(undefined);
DashboardContext.displayName = 'DashboardContext';

export const DashboardProvider: React.FC<Props> = ({ ...props }) => {
  const navigate = useNavigate();
  const { dashboardId } = useParams();
  const status = useStreamV2Status();
  const [isReadyToProcessMessages, setIsReadyToProcessMessages] = useState(false);
  const [needsFittingWidgets, setNeedsFittingWidgets] = useState(false);
  const [selectedFreemiumDashboard, setSelectedFreemiumDashboard] = useState<number>(0);
  const {
    currentDashboard,
    allDashboards,
    setCurrentDashboardById,
    addDashboard: originalAddDashboard,
    updateDashboard,
    deleteDashboard,
    editWidgetPayloadById,
    deleteWidgetById,
    addNewWidgets,
    clearCurrentDashboard,
    isDashboardLoading,
    editDashboardLayouts,
    subscribeToDashboards,
    createDefaultDashboard: originalCreateDefaultDashboard,
    loadDashboard,
  } = useStreamV2DashboardService();

  const {
    sortedDashboardList,
    updateDashboardOrder,
    addNewDashboardToOrder,
    removeDashboardFromOrder,
    loading: dashboardOrderLoading,
  } = useDashboardOrder(allDashboards);

  const addDashboard = async (overrideDashboardName?: string) => {
    const newDashboard = await originalAddDashboard(overrideDashboardName);
    addNewDashboardToOrder(newDashboard as Dashboard);
    return newDashboard;
  };

  const createDefaultDashboard = async () => {
    const newDashboard = await originalCreateDefaultDashboard();
    if (newDashboard) {
      addNewDashboardToOrder(newDashboard);
    }
    return newDashboard;
  };

  const value: DashboardState = {
    currentDashboard,
    allDashboards,
    setCurrentDashboardById,
    addDashboard: addDashboard as (overrideDashboardName?: string) => Promise<Dashboard>,
    deleteDashboard,
    updateDashboard,
    editWidgetPayloadById,
    deleteWidgetById,
    addNewWidgets,
    clearCurrentDashboard,
    isDashboardLoading,
    editDashboardLayouts,
    needsFittingWidgets,
    setNeedsFittingWidgets,
    sortedDashboardList,
    updateDashboardOrder,
    addNewDashboardToOrder,
    removeDashboardFromOrder,
    dashboardOrderLoading,
    createDefaultDashboard,
    loadDashboard,
    selectedFreemiumDashboard,
    setSelectedFreemiumDashboard,
  };

  useEffect(() => {
    setIsReadyToProcessMessages(status === Status.Connected);
  }, [status]);

  useEffect(() => {
    if (!isReadyToProcessMessages) return;
    const { unsubscribe } = subscribeToDashboards(addNewDashboardToOrder);

    return () => {
      unsubscribe();
    };
  }, [isReadyToProcessMessages, subscribeToDashboards, addNewDashboardToOrder]);

  useEffect(() => {
    if (!window.location.pathname.includes('dashboard')) return;
    if (!isReadyToProcessMessages) return;

    if (allDashboards.length > 0) {
      const firstDashboard = allDashboards[0];
      if (dashboardId) {
        const URLDashboard = allDashboards.find(d => d.id === +dashboardId);
        if (URLDashboard) {
          if (URLDashboard.id !== currentDashboard?.id) {
            setCurrentDashboardById(URLDashboard.id.toString());
          }
        } else {
          if (firstDashboard?.id) {
            setCurrentDashboardById(firstDashboard.id.toString());
            navigate(`/dashboard/${firstDashboard.id}`);
          }
        }
      } else {
        if (!currentDashboard) {
          if (firstDashboard?.id) {
            setCurrentDashboardById(firstDashboard.id.toString());
            navigate(`/dashboard/${firstDashboard.id}`);
          }
        }
      }
    }
  }, [currentDashboard, dashboardId, allDashboards, setCurrentDashboardById, isReadyToProcessMessages, navigate]);

  useEffect(() => {
    if (window.location.pathname === '/freemium-news') {
      setSelectedFreemiumDashboard(-1);
    } else if (window.location.pathname.includes('dashboard')) {
      setSelectedFreemiumDashboard(0);
    }
  }, [window.location.pathname]);

  return <DashboardContext.Provider value={value} {...props} />;
};

export const useDashboardContext = (): DashboardState => {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }

  return context;
};
